import VisibilityIcon from "@mui/icons-material/Visibility";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useContext } from "react";
import { Helmet } from "react-helmet-async";

import { AccessContext } from "src/context/AccessContext";
import { AppContext } from "src/context/AppContext";
import { CoursesContext } from "src/context/CoursesContext";
import { useNavigateUnauthenticatedUserToSignUpPage } from "src/hooks/useNavigateUnauthenticatedUserToSignUpPage";
import useResponsive from "src/hooks/useResponsive";
import ResourceModelType from "src/models/ResourceModelType";
import RoutedButton from "src/newComponents/buttons/RoutedButton";

import PageTemplate from "../PageTemplate";
import MyCard from "../products/MyCard";

const href = "/access-free-content/";
const title = "Access Free Content | Pre-Reg Shortcuts";
const description =
  "Join us now and create a free account to access GPhC exam revision courses, question bank and more! Start your foundation training today!";
const keywords =
  "GPhC exam revision, foundation pharmacist training, Pre-Reg Shortcuts, free account, BNF course, calculations course, MEP course, OTC course, Calculations course, designated supervisor programme, pharmacy training, prereg exam, pharmacy, pharmacy revision";

const AccessFreeContentPage = () => {
  useNavigateUnauthenticatedUserToSignUpPage();

  const { name } = useContext(AppContext);
  const { courses } = useContext(CoursesContext);

  const premiumCourses = courses.filter((course) => course.includedInPremium);

  const { getResourceModelByResourceType } = useContext(AccessContext);
  const qbResourceModel = getResourceModelByResourceType(ResourceModelType.questionBank);

  const ftpResourceModel = getResourceModelByResourceType(ResourceModelType.foundationTrainingProgramme);
  const dspResourceModel = getResourceModelByResourceType(ResourceModelType.designatedSupervisorProgramme);

  return (
    <>
      <Helmet link={[{ rel: "canonical", href: href }]}>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
      </Helmet>
      <PageTemplate>
        <Typography variant="h1" style={{ fontSize: "28px", textAlign: "center" }}>
          Welcome back, {name}!
        </Typography>

        <Typography variant="h1" style={{ fontSize: "28px", textAlign: "center" }} sx={{ mb: 2, mt: 1 }}>
          Access FREE Foundation Training Year and exam revision resources with Pre-Reg Shortcuts
        </Typography>

        <Typography variant="h2" style={{ fontSize: "24px" }} sx={{ mb: 2 }}>
          Pharmacy Students & Trainee Pharmacists: Access exam revision courses and question bank
        </Typography>

        <Typography sx={{ mb: 2 }}>
          Select your course, then chapter to preview free lessons and start your revision. Access free questions from
          our 1000+ GPhC style question bank.
        </Typography>

        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 6 }}>
          {premiumCourses.map((course, idx) => {
            return (
              <Grid item key={idx} xs={12} sm={4} sx={{ display: "flex" }}>
                <Section resourceId={course.resourceId} title={course.title} link={`/courses/${course.slug}`} />
              </Grid>
            );
          })}
          <Grid item key={"question-bank"} xs={12} sm={4} sx={{ display: "flex" }}>
            <Section resourceId={qbResourceModel?.id} title={"Question Bank"} link="/question-bank/" />
          </Grid>
        </Grid>

        <Typography variant="h1" style={{ fontSize: "28px", textAlign: "center" }} sx={{ mb: 2 }}>
          Extra Features:
        </Typography>

        <Typography sx={{ mb: 2 }}>
          Interested in extra support features including workbook answers discussed in the 50+ live webinars, telegram
          support group, revision checklists and more? Available on the Foundation Pharmacist Training Programme
        </Typography>

        <Typography variant="h2" style={{ fontSize: "24px" }} sx={{ mb: 2 }}>
          Trainee Pharmacists & Pharmacy Owners/Organisations: Preview our Foundation Pharmacist Training and Designated
          Supervisor Programme
        </Typography>

        <Typography sx={{ mb: 2 }}>
          All the resources above are included, along with live support throughout your Foundation Training Year
        </Typography>

        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 6 }}>
          <Grid item key={"ftp"} xs={12} sm={4} sx={{ display: "flex" }}>
            <Section
              resourceId={ftpResourceModel?.id}
              title={"Foundation Pharmacist Training Programme"}
              link="/programme/"
            />
          </Grid>
          <Grid item key={"dsp"} xs={12} sm={4} sx={{ display: "flex" }}>
            <Section
              resourceId={dspResourceModel?.id}
              title={"Designated Supervisor Programme"}
              link="/designated-supervisor-programme/"
            />
          </Grid>
        </Grid>

        <Typography variant="h2" style={{ fontSize: "24px" }} sx={{ mb: 2 }}>
          Pharmacy Students: ORIEL support and Graduate to Trainee Pharmacist Workshops (Coming Soon)
        </Typography>
      </PageTemplate>
    </>
  );
};

export { AccessFreeContentPage };

interface SectionProps {
  resourceId: number | null | undefined;
  title: string;
  link: string;
}

const Section = ({ resourceId, title, link }: SectionProps) => {
  const { getResourceModelByResourceId } = useContext(AccessContext);
  const isSmUp = useResponsive("up", "sm");
  const isMdUp = useResponsive("up", "md");

  if (resourceId === null || resourceId === undefined) return null;

  const resourceModel = getResourceModelByResourceId(resourceId);

  const getIconWidth = (isSmUp: boolean, isMdUp: boolean) => {
    if (isMdUp) return "60%";
    if (isSmUp) return "80%";
    return "55%";
  };

  const iconWidth = getIconWidth(isSmUp, isMdUp);

  return (
    <MyCard
      customStyle={{
        padding: "0",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: "100%", // Make the card take full width
      }}
    >
      <div style={{ display: "flex", justifyContent: "center" }}>
        <img
          src={resourceModel?.image}
          alt={title}
          title={title}
          style={{ width: iconWidth, height: "auto" }}
          loading="eager"
        />
      </div>

      <div style={{ padding: "1rem 1rem 0 1rem" }}>
        <RoutedButton
          path={link}
          variant="outlined"
          startIcon={<VisibilityIcon />}
          buttonColor="primary"
          size="small"
          buttonStyle={{ width: "100%", height: "100%" }}
        >
          View {title}
        </RoutedButton>
      </div>
    </MyCard>
  );
};
